import React, { useState, useMemo, useEffect } from "react";
import {
  Alert,
  Button,
  Card,
  Form,
  Modal,
  Spinner,
  Table,
} from "react-bootstrap";
import Select from "react-select";
import toast, { Toaster } from "react-hot-toast";
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
} from "@tanstack/react-table";
import dayjs from "dayjs";
import { useSearchParams } from "react-router-dom";
import { useRegisterPayment } from "../hooks/useRegisterPayment";
import { useOwnerPayments } from "../hooks/useOwnerPayments";
import { useMemberships } from "../hooks/useMemberships";
import { usePackages } from "../hooks/usePackages";
import { useNextPeriod } from "../hooks/useNextPeriod";
import { DateField } from "../../../components/DateField";
import { PriceWithDiscount } from "../../../components/PriceWithDiscount";

const COP = new Intl.NumberFormat("es-CO", {
  style: "currency",
  currency: "COP",
  maximumFractionDigits: 0,
});

const PAYMENT_METHODS = [
  { value: "CASH", label: "Efectivo" },
  { value: "TRANSFER", label: "Transferencia" },
  { value: "CARD", label: "Tarjeta" },
  { value: "OTHER", label: "Otro" },
];

const INITIAL_FORM = {
  studentOption: null,
  packageOption: null,
  amount: "",
  methodOption: null,
  // Fechas como "YYYY-MM-DD" (contrato de DateField, compatible con el backend).
  periodFrom: "",
  periodTo: "",
  date: dayjs().format("YYYY-MM-DD"),
};

// Maps backend field names (from field_errors) to the form field labels and to
// the form-state key, so an API error can name the field and mark it in red.
const FIELD_META = {
  student: { label: "Estudiante" },
  package: { label: "Paquete" },
  amount: { label: "Monto" },
  method: { label: "Método" },
  period_start: { label: "Periodo desde" },
  period_end: { label: "Periodo hasta" },
  receipt_number: { label: "N.º de recibo" },
  date: { label: "Fecha de pago" },
};

const PAYMENT_COLUMNS = [
  {
    accessorKey: "date",
    header: "Fecha",
    cell: ({ getValue }) => dayjs(getValue()).format("D MMM YYYY"),
  },
  {
    id: "student_name",
    header: "Estudiante",
    cell: ({ row }) =>
      row.original.student?.full_name ?? row.original.student?.email ?? "—",
  },
  {
    accessorKey: "amount",
    header: "Monto",
    cell: ({ getValue }) => COP.format(getValue()),
  },
  {
    accessorKey: "method",
    header: "Método",
    cell: ({ getValue }) =>
      PAYMENT_METHODS.find((m) => m.value === getValue())?.label ?? getValue(),
  },
  {
    accessorKey: "receipt_number",
    header: "Recibo",
  },
];

function PagosPage() {
  const [searchParams] = useSearchParams();
  const preselectedStudentId = searchParams.get("student");

  const [form, setForm] = useState(INITIAL_FORM);
  const [fieldErrors, setFieldErrors] = useState({});
  const [receiptPayment, setReceiptPayment] = useState(null);

  const { data: memberships, isLoading: membersLoading } =
    useMemberships("STUDENT");
  const { data: packages, isLoading: packagesLoading } = usePackages();
  const { data: payments, isLoading: paymentsLoading } = useOwnerPayments();
  const registerMutation = useRegisterPayment();

  const studentOptions = useMemo(
    () =>
      (memberships ?? []).map((m) => ({
        // Payment.subscription.enrollment.student is a Profile → use profile id.
        value: m.profile ?? m.user?.id ?? m.id,
        label: m.user?.full_name ?? m.user?.email ?? `Estudiante ${m.id}`,
        membership: m,
      })),
    [memberships]
  );

  const packageOptions = useMemo(
    () =>
      (packages ?? []).map((p) => ({
        value: p.id,
        // Texto plano: lo usa react-select internamente para filtrar al
        // escribir. La versión visual (con el precio con descuento) va en
        // formatOptionLabel más abajo.
        label: `${p.name} · ${p.hours_included} h`,
        name: p.name,
        hours_included: p.hours_included,
        // El monto a cobrar (prellenado y tope del campo Monto) es el precio
        // CON descuento — antes se usaba p.price sin más y el descuento del
        // paquete no tenía ningún efecto en lo que el estudiante pagaba.
        price: p.final_price,
        list_price: p.price,
        discount_pct: p.discount_pct,
        validity_days: p.validity_days,
      })),
    [packages]
  );

  // Auto-compute the period so packages chain after the student's latest one.
  const { data: nextPeriod } = useNextPeriod({
    student: form.studentOption?.value,
    pkg: form.packageOption?.value,
  });

  useEffect(() => {
    if (!nextPeriod?.period_start || !nextPeriod?.period_end) return;
    setForm((prev) => ({
      ...prev,
      periodFrom: nextPeriod.period_start,
      periodTo: nextPeriod.period_end,
    }));
  }, [nextPeriod?.period_start, nextPeriod?.period_end]);

  // The amount can't exceed the selected package's price.
  const packagePrice = form.packageOption?.price ?? null;
  const amountExceedsPackage =
    packagePrice != null &&
    form.amount !== "" &&
    parseFloat(form.amount) > Number(packagePrice);

  // Pre-select student from URL param (e.g. navigated from CarteraPage)
  useEffect(() => {
    if (!preselectedStudentId || !studentOptions.length) return;
    const match = studentOptions.find(
      (o) => String(o.value) === preselectedStudentId
    );
    if (match) {
      setForm((prev) => ({ ...prev, studentOption: match }));
    }
  }, [preselectedStudentId, studentOptions]);

  const table = useReactTable({
    data: payments ?? [],
    columns: PAYMENT_COLUMNS,
    getCoreRowModel: getCoreRowModel(),
  });

  // Setting any field clears its pending error so the red mark disappears as
  // the user corrects it.
  const setField = (field, errorKey) => (value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errorKey) {
      setFieldErrors((prev) => {
        if (!prev[errorKey]) return prev;
        const next = { ...prev };
        delete next[errorKey];
        return next;
      });
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setFieldErrors({});
    if (
      !form.studentOption ||
      !form.packageOption ||
      !form.amount ||
      !form.methodOption
    ) {
      toast.error("Completa los campos obligatorios.");
      return;
    }

    if (amountExceedsPackage) {
      toast.error("El monto no puede ser mayor al valor del paquete.");
      return;
    }

    registerMutation.mutate(
      {
        student: form.studentOption.value,
        package: form.packageOption.value,
        amount: parseFloat(form.amount),
        method: form.methodOption.value,
        period_start: form.periodFrom || null,
        period_end: form.periodTo || null,
        // El recibo lo asigna el backend automáticamente (consecutivo por academia).
        date: form.date || undefined,
      },
      {
        onSuccess: () => {
          toast.success("Pago registrado con éxito.");
          setForm(INITIAL_FORM);
        },
        onError: (err) => {
          const data = err?.response?.data;
          const errors = data?.field_errors ?? {};
          setFieldErrors(errors);
          const firstKey = Object.keys(errors)[0];
          if (firstKey) {
            const label = FIELD_META[firstKey]?.label ?? firstKey;
            toast.error(`${label}: ${errors[firstKey][0]}`);
          } else {
            toast.error(
              data?.detail ?? "Error al registrar el pago. Inténtalo de nuevo."
            );
          }
        },
      }
    );
  };

  // Inline red error message for a given backend field key.
  const fieldError = (key) =>
    fieldErrors[key] ? (
      <div className="text-danger small mt-1">{fieldErrors[key][0]}</div>
    ) : null;

  return (
    <>
      <Toaster position="top-right" />
      <div className="container-fluid px-3">
        <div className="py-3">
          <h1 className="h4 mb-0">Registro de pagos</h1>
          <p className="text-muted small mb-0">
            Registra el pago de un estudiante (sin pasarela de pago)
          </p>
        </div>

        {/* Registration form */}
        <Card className="mb-4">
          <Card.Header>
            <strong>Nuevo pago</strong>
          </Card.Header>
          <Card.Body>
            <Form onSubmit={handleSubmit}>
              {/* Single column on mobile */}
              <div className="row g-3">
                <div className="col-12 col-md-6">
                  <Form.Label className="fw-medium">
                    Estudiante <span className="text-danger">*</span>
                  </Form.Label>
                  <Select
                    options={studentOptions}
                    value={form.studentOption}
                    onChange={setField("studentOption", "student")}
                    placeholder={
                      membersLoading ? "Cargando…" : "Buscar estudiante"
                    }
                    isLoading={membersLoading}
                    classNamePrefix="react-select"
                    inputId="student-select"
                  />
                  {fieldError("student")}
                </div>

                <div className="col-12 col-md-6">
                  <Form.Label className="fw-medium">
                    Paquete <span className="text-danger">*</span>
                  </Form.Label>
                  <Select
                    options={packageOptions}
                    value={form.packageOption}
                    onChange={(opt) =>
                      setForm((prev) => ({
                        ...prev,
                        packageOption: opt,
                        // Set the amount to the selected package's price (still
                        // editable afterward for discounts/adjustments).
                        amount: opt ? String(opt.price) : prev.amount,
                      }))
                    }
                    placeholder={
                      packagesLoading ? "Cargando…" : "Seleccionar paquete"
                    }
                    isLoading={packagesLoading}
                    noOptionsMessage={() => "No hay paquetes creados"}
                    classNamePrefix="react-select"
                    inputId="package-select"
                    formatOptionLabel={(opt) => (
                      <div className="d-flex justify-content-between align-items-center gap-2">
                        <span>
                          {opt.name} · {opt.hours_included} h
                        </span>
                        <PriceWithDiscount
                          listPrice={opt.list_price}
                          discountPct={opt.discount_pct}
                          finalPrice={opt.price}
                          size="sm"
                        />
                      </div>
                    )}
                  />
                  {fieldError("package")}
                </div>

                <div className="col-12 col-md-3">
                  <Form.Label className="fw-medium">
                    Monto (COP) <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="number"
                    min="0"
                    max={packagePrice ?? undefined}
                    step="1000"
                    placeholder="200000"
                    value={form.amount}
                    onChange={(e) => setField("amount", "amount")(e.target.value)}
                    isInvalid={Boolean(fieldErrors.amount) || amountExceedsPackage}
                    required
                  />
                  {amountExceedsPackage ? (
                    <Form.Control.Feedback type="invalid" className="d-block">
                      No puede ser mayor al valor del paquete (
                      {COP.format(packagePrice)}).
                    </Form.Control.Feedback>
                  ) : (
                    fieldError("amount")
                  )}
                </div>

                <div className="col-12 col-md-3">
                  <Form.Label className="fw-medium">
                    Método <span className="text-danger">*</span>
                  </Form.Label>
                  <Select
                    options={PAYMENT_METHODS}
                    value={form.methodOption}
                    onChange={setField("methodOption", "method")}
                    placeholder="Seleccionar"
                    classNamePrefix="react-select"
                    inputId="method-select"
                  />
                  {fieldError("method")}
                </div>

                <div className="col-6 col-md-3">
                  <Form.Label className="fw-medium">Periodo desde</Form.Label>
                  <DateField
                    value={form.periodFrom}
                    onChange={setField("periodFrom", "period_start")}
                    max={form.periodTo}
                  />
                  {fieldError("period_start")}
                </div>

                <div className="col-6 col-md-3">
                  <Form.Label className="fw-medium">Periodo hasta</Form.Label>
                  <DateField
                    value={form.periodTo}
                    onChange={setField("periodTo", "period_end")}
                    min={form.periodFrom}
                  />
                  {fieldError("period_end")}
                </div>

                <div className="col-12 col-md-3">
                  <Form.Label className="fw-medium">Fecha de pago</Form.Label>
                  <DateField
                    value={form.date}
                    onChange={setField("date", "date")}
                  />
                  {fieldError("date")}
                </div>
              </div>

              <div className="mt-3">
                <Button
                  type="submit"
                  variant="primary"
                  style={{ minHeight: 44 }}
                  disabled={registerMutation.isPending}
                  data-testid="submit-payment"
                >
                  {registerMutation.isPending ? (
                    <Spinner animation="border" size="sm" />
                  ) : (
                    "Registrar pago"
                  )}
                </Button>
              </div>

              {registerMutation.error && (
                <Alert variant="danger" className="mt-3 mb-0">
                  {registerMutation.error?.response?.data?.detail ??
                    "Error al registrar el pago."}
                </Alert>
              )}
            </Form>
          </Card.Body>
        </Card>

        {/* Payments list */}
        <Card>
          <Card.Header>
            <strong>Pagos registrados</strong>
          </Card.Header>

          {paymentsLoading && (
            <div className="text-center py-4">
              <Spinner animation="border" size="sm" />
            </div>
          )}

          {!paymentsLoading && (payments ?? []).length === 0 && (
            <Card.Body>
              <p className="text-muted mb-0">No hay pagos registrados aún.</p>
            </Card.Body>
          )}

          {!paymentsLoading && (payments ?? []).length > 0 && (
            <>
              {/* PC: tabla */}
              <div className="table-responsive d-none d-md-block">
                <Table hover className="mb-0">
                  <thead className="table-light">
                    {table.getHeaderGroups().map((hg) => (
                      <tr key={hg.id}>
                        {hg.headers.map((h) => (
                          <th key={h.id}>
                            {flexRender(
                              h.column.columnDef.header,
                              h.getContext()
                            )}
                          </th>
                        ))}
                        <th>Acciones</th>
                      </tr>
                    ))}
                  </thead>
                  <tbody>
                    {table.getRowModel().rows.map((row) => (
                      <tr key={row.id}>
                        {row.getVisibleCells().map((cell) => (
                          <td key={cell.id}>
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext()
                            )}
                          </td>
                        ))}
                        <td>
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            style={{ minHeight: 44 }}
                            onClick={() => setReceiptPayment(row.original)}
                            data-testid={`ver-recibo-${row.original.id}`}
                          >
                            Ver recibo
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>

              {/* Móvil: tarjetas */}
              <div className="d-md-none">
                {table.getRowModel().rows.map((row) => {
                  const p = row.original;
                  const method =
                    PAYMENT_METHODS.find((m) => m.value === p.method)?.label ??
                    p.method;
                  return (
                    <div key={row.id} className="border-bottom p-3">
                      <div className="d-flex justify-content-between gap-2">
                        <div style={{ minWidth: 0 }}>
                          <div className="fw-medium">
                            {p.student?.full_name ?? p.student?.email ?? "—"}
                          </div>
                          <div className="text-muted small">
                            {dayjs(p.date).format("D MMM YYYY")} · {method}
                          </div>
                          <div className="text-muted small">
                            Recibo {p.receipt_number}
                          </div>
                        </div>
                        <div className="text-end flex-shrink-0">
                          <div className="fw-bold text-nowrap">
                            {COP.format(p.amount)}
                          </div>
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            className="mt-2"
                            style={{ minHeight: 44 }}
                            onClick={() => setReceiptPayment(p)}
                            data-testid={`ver-recibo-m-${p.id}`}
                          >
                            Ver recibo
                          </Button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </Card>
      </div>

      {/* Receipt modal — no external PDF; TASK-19 placeholder for PDF */}
      <Modal
        show={!!receiptPayment}
        onHide={() => setReceiptPayment(null)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Recibo de pago</Modal.Title>
        </Modal.Header>
        <Modal.Body data-testid="receipt-modal">
          {receiptPayment && (
            <dl className="row mb-0">
              <dt className="col-5">Estudiante</dt>
              <dd className="col-7">
                {receiptPayment.student?.full_name ??
                  receiptPayment.student?.email ??
                  "—"}
              </dd>
              <dt className="col-5">Fecha</dt>
              <dd className="col-7">
                {dayjs(receiptPayment.date).format("D MMM YYYY")}
              </dd>
              <dt className="col-5">Monto</dt>
              <dd className="col-7 text-success fw-bold">
                {COP.format(receiptPayment.amount)}
              </dd>
              <dt className="col-5">Método</dt>
              <dd className="col-7">
                {PAYMENT_METHODS.find((m) => m.value === receiptPayment.method)
                  ?.label ?? receiptPayment.method}
              </dd>
              {receiptPayment.period_start && (
                <>
                  <dt className="col-5">Periodo</dt>
                  <dd className="col-7">
                    {dayjs(receiptPayment.period_start).format("D MMM YYYY")} —{" "}
                    {dayjs(receiptPayment.period_end).format("D MMM YYYY")}
                  </dd>
                </>
              )}
              {receiptPayment.receipt_number && (
                <>
                  <dt className="col-5">N.º recibo</dt>
                  <dd className="col-7">{receiptPayment.receipt_number}</dd>
                </>
              )}

              {receiptPayment.package && (
                <>
                  <dt className="col-12 mt-3 text-uppercase text-muted small">
                    Paquete
                  </dt>
                  <dt className="col-5">Paquete</dt>
                  <dd className="col-7">{receiptPayment.package.name ?? "—"}</dd>
                  <dt className="col-5">Horas incluidas</dt>
                  <dd className="col-7">
                    {receiptPayment.package.hours_included != null
                      ? `${receiptPayment.package.hours_included} h`
                      : "—"}
                  </dd>
                  {receiptPayment.package.final_price != null && (
                    <>
                      <dt className="col-5">Precio</dt>
                      <dd className="col-7">
                        <PriceWithDiscount
                          listPrice={receiptPayment.package.list_price}
                          discountPct={receiptPayment.package.discount_pct}
                          finalPrice={receiptPayment.package.final_price}
                          size="sm"
                        />
                      </dd>
                    </>
                  )}
                  <dt className="col-5">Saldo actual</dt>
                  <dd className="col-7">
                    {receiptPayment.package.hours_purchased != null
                      ? `${receiptPayment.package.hours_balance} / ${receiptPayment.package.hours_purchased} h`
                      : "—"}
                  </dd>
                  <dt className="col-5">Estado</dt>
                  <dd className="col-7">
                    {receiptPayment.package.status_display ??
                      receiptPayment.package.status}
                  </dd>
                  {receiptPayment.package.end_date && (
                    <>
                      <dt className="col-5">Vence</dt>
                      <dd className="col-7">
                        {dayjs(receiptPayment.package.end_date).format(
                          "D MMM YYYY"
                        )}
                      </dd>
                    </>
                  )}
                </>
              )}
            </dl>
          )}
          {/* TASK-19: PDF de recibo — ver placeholder aquí */}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            style={{ minHeight: 44 }}
            onClick={() => setReceiptPayment(null)}
          >
            Cerrar
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export { PagosPage };
